// =============================================================================
// apiError — turn fetch failures into human-readable messages instead of
// surfacing raw "HTTP 404" style codes to the user.
//   humanError(res, body, fallback) — for a completed response that wasn't ok
//   errorText(err, fallback)        — for a thrown error (incl. network failures)
// Always prefers an explicit message from the server when present.
// =============================================================================

// Server/Epicor errors often arrive as a JSON envelope or carry \r\n noise.
// Extract the meaningful sentence(s) and drop the technical wrapper so the
// user sees "Rep. ID references invalid value." not a raw exception blob.
export function cleanMessage(raw) {
  if (!raw || typeof raw !== "string") return raw;
  let text = raw.trim();
  if (text.startsWith("{") || text.startsWith("[")) {
    try {
      const o = JSON.parse(text);
      const pickFrom = (x) => x && (x.ErrorMessage || x.errorMessage || x.Message || x.message
        || (Array.isArray(x.ErrorDetails) && x.ErrorDetails[0] && (x.ErrorDetails[0].Message || x.ErrorDetails[0].message)));
      text = pickFrom(o) || pickFrom(Array.isArray(o) ? o[0] : {}) || text;
    } catch { /* not valid JSON — fall through with the raw text */ }
  }
  // Collapse CR/LF (literal or real) into clean separators and trim noise.
  text = String(text).replace(/\\r\\n|\\n|\\r|\r\n|\n|\r/g, " ").replace(/\s{2,}/g, " ").trim();
  // If, after all that, it's still a JSON-looking blob, give up gracefully.
  if (text.startsWith("{") && text.length > 200) return "The request was rejected by the server. Please review the details and try again.";
  return text;
}

export function humanError(res, body, fallback) {
  const raw = body && (body.message || body.error || body.failedReason || body.detail);
  if (raw && typeof raw === "string" && raw.trim() && !/^HTTP\s+\d+$/i.test(raw.trim())) {
    return cleanMessage(raw);
  }
  const code = res && res.status;
  switch (code) {
    case 400: return fallback || "The request couldn't be processed. Please check the details and try again.";
    case 401: return "Your session has expired — please sign in again.";
    case 403: return "You don't have permission to do that.";
    case 404: return fallback || "We couldn't find what you were looking for.";
    case 409: return fallback || "That conflicts with the current data. Refresh and try again.";
    case 408:
    case 502:
    case 503:
    case 504: return "The service is temporarily unavailable. Please try again in a moment.";
    default:
      if (code >= 500) return "Something went wrong on the server. Please try again.";
      return fallback || "The request failed. Please try again.";
  }
}

// fetch() rejects with a TypeError ("Failed to fetch") on network/CORS errors.
export function isNetworkError(e) {
  return e instanceof TypeError || /failed to fetch|networkerror|load failed/i.test((e && e.message) || "");
}

export function errorText(e, fallback) {
  if (isNetworkError(e)) return "Couldn't reach the server. Check your connection and try again.";
  return cleanMessage((e && e.message)) || fallback || "Something went wrong. Please try again.";
}
